import { useCallback, useState, type ReactNode } from 'react'

import { ShipmentType, TmsErrorCode } from '@b-stock/shipment-api-client'

import { detectParcelNoRatesCode } from './detectNoRates'
import { ParcelNoRatesBanner } from './ParcelNoRatesBanner'
import { ParcelRateList } from './ParcelRateList'
import { ParcelRateSection } from './ParcelRateSection'
import type { ParcelNoRatesErrorCode, ParcelQuote } from './types'

export type UseParcelRateFlowParams = {
  /** `enable-parcel-quotes` flag AND the listing is Binding + Parcel. */
  showParcelRates: boolean
  /** Full parcel quote set from `quoteSetQuery` (undefined while loading/errored). */
  quoteSet: ParcelQuote[] | undefined
  /**
   * The buyer's selected quote. `quoteSetQuery` drops zero-cost quotes, so when
   * the only quote is the backend's zero-quote the set arrives empty and this
   * is where its error code is read from.
   */
  selectedQuote: ParcelQuote | null | undefined
  /** `quoteSetQuery` is on its first load with no data yet. */
  isQuoteSetLoading: boolean
  /** `quoteSetQuery` failed (network / 5xx / transport). */
  isQuoteSetError: boolean
  /** The shared carrier-change mutation is in flight. */
  isCarrierChangePending: boolean
  /** Persist the buyer's rate choice (reuses the carrier-change mutation). */
  setSelectedCarrier: (quoteId: string) => Promise<void>
  currency: string
  /** Replaces the error-derived banner text when a rate selection fails. */
  selectErrorMessage?: string
}

type UseParcelRateFlowResult = {
  /** The inline rate list, the blocking banner, or null. Render above the order total. */
  parcelRateList: ReactNode
  /** No usable rate (zero-quote error code or query failure) — block payment with a reason. */
  parcelPaymentBlocked: boolean
  /** A rate selection is in flight — disable Confirm (and show its spinner). */
  isParcelSelectionPending: boolean
  /** The quote set is loading for the first time — disable Confirm (no spinner) until it resolves. */
  isParcelRatesLoading: boolean
}

/**
 * Shared parcel rate logic for the Bid and Buy-Now confirmation modals and the
 * order details carrier change.
 *
 * Centralizes rate-set rendering, selection, and the payment-blocking states so
 * both modals stay in lockstep:
 * - A backend zero-quote placeholder (`tmsMetaData.errorCode`) → blocking banner
 *   with the per-code copy (FP-1906).
 * - A quote-set **query failure** (`isQuoteSetError`) → blocking banner using the
 *   `PARCEL_VENDOR_UNAVAILABLE` ("temporarily unavailable") copy, since it's a
 *   transient/retryable condition rather than a definitive "no rates". Without
 *   this the buyer would fail open: no list, no banner, Confirm enabled.
 * - The initial quote-set **load** (`isQuoteSetLoading`) → Confirm disabled (no
 *   banner) so the buyer can't submit before the rate UI has rendered.
 * - A `selectedQuote` of any type other than Binding hides the rate list and
 *   never blocks payment.
 */
export const useParcelRateFlow = ({
  showParcelRates,
  quoteSet,
  selectedQuote,
  isQuoteSetLoading,
  isQuoteSetError,
  isCarrierChangePending,
  setSelectedCarrier,
  currency,
  selectErrorMessage,
}: UseParcelRateFlowParams): UseParcelRateFlowResult => {
  // Track our own in-flight boolean so Confirm/cards disable immediately, even
  // before the shared mutation's `isCarrierChangePending` flips on the next render.
  const [isParcelSelectPending, setIsParcelSelectPending] = useState(false)
  const isParcelSelectionPending =
    isParcelSelectPending || isCarrierChangePending
  const showRates =
    showParcelRates &&
    (!selectedQuote || selectedQuote.shipmentType === ShipmentType.Binding)

  const handleParcelRateSelect = useCallback(
    async (quoteId: string) => {
      setIsParcelSelectPending(true)
      try {
        await setSelectedCarrier(quoteId)
      } finally {
        setIsParcelSelectPending(false)
      }
    },
    [setSelectedCarrier]
  )

  // No usable rate. A present quote set is classified by the backend's
  // zero-quote error code; an absent set due to a query failure maps to the
  // retryable "vendor unavailable" copy. A still-loading set is neither.
  const noRatesQuotes =
    quoteSet?.length === 0 && selectedQuote ? [selectedQuote] : quoteSet
  const parcelNoRatesCode: ParcelNoRatesErrorCode | null = !showRates
    ? null
    : noRatesQuotes
      ? detectParcelNoRatesCode(noRatesQuotes)
      : isQuoteSetError
        ? TmsErrorCode.ParcelVendorUnavailable
        : null
  const parcelPaymentBlocked = parcelNoRatesCode !== null

  const isParcelRatesLoading = showRates && isQuoteSetLoading

  // The list or the blocking banner — whichever applies — rendered under a
  // shared "Shipping Options" section header (so the no-rates state stays
  // contextualized). Pure loading renders nothing (Confirm is just disabled).
  const content: ReactNode = parcelNoRatesCode ? (
    <ParcelNoRatesBanner errorCode={parcelNoRatesCode} />
  ) : quoteSet && quoteSet.length > 0 ? (
    <ParcelRateList
      quotes={quoteSet}
      currency={currency}
      onSelectQuote={handleParcelRateSelect}
      isSelectionPending={isCarrierChangePending}
      selectErrorMessage={selectErrorMessage}
    />
  ) : null

  const parcelRateList: ReactNode =
    showRates && content ? (
      <ParcelRateSection>{content}</ParcelRateSection>
    ) : null

  return {
    parcelRateList,
    parcelPaymentBlocked,
    isParcelSelectionPending,
    isParcelRatesLoading,
  }
}
